import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, List, ListItem } from '@mui/material';

import FullSizeModalSlider from '@components/FullSizeModalSlider/FullSizeModalSlider';
import ImageSlickCarousel from '@components/ImageMultiCarousel/ImageMultiCarousel';
import ViewLinkButton from '@components/ViewLinkButton/ViewLinkButton';
import { IRiskSupplementImagesResponseDTO } from '@interfaces/highRiskSupplements';
import { HighRiskSupplementDetails } from '@models/highRiskSupplementDetails';
import formatDate from '@utils/formatDate';

import WarningTypeItemWithAccordion from '../WarningTypeItemWithAccordion/WarningTypeItemWithAccordion';

import './style.scss';

const imagesSliderResponsiveSettings = [
  {
    breakpoint: 400,
    settings: {
      slidesToShow: 1,
    },
  },
  {
    breakpoint: 576,
    settings: {
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 767,
    settings: {
      slidesToShow: 4,
    },
  },
  {
    breakpoint: 992,
    settings: {
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 1080,
    settings: {
      slidesToShow: 7,
    },
  },
];

interface IProps {
  details: HighRiskSupplementDetails;
}

// HRS stands for High Risk Supplements
const HRSDetailsCard: FC<IProps> = ({ details }): JSX.Element => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLInputElement | null>(null);
  const [selectedImage, setSelectedImage] = useState<IRiskSupplementImagesResponseDTO | null>(null);

  const closeSlider = useCallback(() => setSelectedImage(null), []);

  const imagesForFullPageSlider = useMemo(() => {
    if (!Array.isArray(details?.images)) {
      return [];
    }
    return details?.images.map(item => ({
      origFileName: item.name,
      url: item.url,
    }));
  }, [details]);

  const initialSlide = useMemo(() => {
    const index = imagesForFullPageSlider.findIndex(item => item.url === selectedImage?.url);
    if (typeof index === 'number' && index >= 0) {
      return index;
    }
    return 0;
  }, [selectedImage, imagesForFullPageSlider]);

  return (
    <>
      <Box>
        <h3 className="hrs-details-card__title">{details.name}</h3>
        {details.riskSupplementCategory && (
          <p className="hrs-details-card__text">
            {t('common.category')}: {details.riskSupplementCategory}
          </p>
        )}
        {details.statusDate && (
          <p className="hrs-details-card__text">
            {t('common.statusDate')}: {formatDate(details.statusDate)}
          </p>
        )}
        <Box className="hrs-details-card__button-holder">
          <ViewLinkButton
            linkToProductPage={details.productInfo}
            buttonTitle={t('common.productInfo')}
          />
          {!!details.fdaLetter && (
            <ViewLinkButton
              linkToProductPage={details.fdaLetter}
              buttonTitle={t('common.fdaLetter')}
            />
          )}
        </Box>
        {Boolean(details.images.length) && (
          <Box className="hrs-details-card__images-holder" ref={containerRef}>
            <ImageSlickCarousel
              slidesToShow={3}
              containerRef={containerRef}
              responsive={imagesSliderResponsiveSettings}
            >
              {details.images.map(image => (
                <Box
                  className="hrs-details-card__slide-holder"
                  onClick={() => setSelectedImage(image)}
                >
                  <Box width={100} height={100} key={image.id} component="img" src={image.url} />
                </Box>
              ))}
            </ImageSlickCarousel>
          </Box>
        )}
        {details.warningTypes.map(warningType => (
          <Box className="hrs-details-card__warning-type-holder">
            <WarningTypeItemWithAccordion data={warningType} />
          </Box>
        ))}
        {Boolean(details.drugClasses.length) && (
          <Box className="hrs-details-card__content-holder">
            <p className="hrs-details-card__title">
              {t('highRiskSupplements.prohibitedDrugClass')}:
            </p>
            <List className="hrs-details-card__list">
              {details.drugClasses.map(drugClass => (
                <ListItem className="hrs-details-card__list-item">{drugClass}</ListItem>
              ))}
            </List>
          </Box>
        )}
        {Boolean(details.ingredients.length) && (
          <>
            <p className="hrs-details-card__title">
              {t('highRiskSupplements.prohibitedIngredients')}:
            </p>
            <List className="hrs-details-card__list">
              {details.ingredients.map(drugClass => (
                <ListItem className="hrs-details-card__list-item">{drugClass}</ListItem>
              ))}
            </List>
          </>
        )}

        {Boolean(details.comments) && (
          <Box className="hrs-details-card__content-holder">
            <p className="hrs-details-card__title">{t('common.comments')}:</p>
            <p className="hrs-details-card__text">{details.comments}</p>
          </Box>
        )}
      </Box>

      {!!selectedImage && (
        <FullSizeModalSlider
          images={imagesForFullPageSlider || []}
          initialSlide={initialSlide}
          handleClose={closeSlider}
        />
      )}
    </>
  );
};

export default HRSDetailsCard;
